import React, { useState } from 'react';

const StarIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg width="40" height="38" viewBox="0 0 40 38" fill={filled ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 1.5l5.09 10.32 11.39 1.66-8.24 8.03 1.94 11.34L20 27.27l-10.18 5.58 1.94-11.34-8.24-8.03 11.39-1.66L20 1.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

interface StarRatingProps {
  questionId: number;
  questionLabelId: string;
  minScore: number;
  maxScore: number;
  minLabel: string;
  maxLabel: string;
  value: number | undefined;
  onChange: (questionId: number, score: number) => void;
}

export const StarRating: React.FC<StarRatingProps> = ({
  questionId,
  questionLabelId,
  minScore,
  maxScore,
  minLabel,
  maxLabel,
  value,
  onChange,
}) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const starCount = maxScore - minScore + 1;

  const handleKeyDown = (e: React.KeyboardEvent, score: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(score + 1, maxScore);
      onChange(questionId, next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const prev = Math.max(score - 1, minScore);
      onChange(questionId, prev);
    }
  };

  return (
    <div className="chatbot-star-rating" role="radiogroup" aria-labelledby={questionLabelId}>
      <div className="chatbot-star-rating-stars">
        {Array.from({ length: starCount }, (_, i) => {
          const score = minScore + i;
          const isFilled = (hoverValue !== null ? score <= hoverValue : value !== undefined && score <= value);
          return (
            <button
              key={score}
              type="button"
              className={`chatbot-star ${isFilled ? 'chatbot-star-filled' : ''}`}
              role="radio"
              aria-checked={value === score}
              aria-label={`${score}/${maxScore}`}
              aria-posinset={i + 1}
              aria-setsize={starCount}
              tabIndex={value === score || (!value && score === minScore) ? 0 : -1}
              onClick={() => onChange(questionId, score)}
              onMouseEnter={() => setHoverValue(score)}
              onMouseLeave={() => setHoverValue(null)}
              onKeyDown={(e) => handleKeyDown(e, score)}
            >
              <StarIcon filled={isFilled} />
            </button>
          );
        })}
      </div>
      <div className="chatbot-star-rating-labels">
        <span className="chatbot-star-rating-label-min">{minLabel}</span>
        <span className="chatbot-star-rating-label-max">{maxLabel}</span>
      </div>
    </div>
  );
};
